<template>
  <span class="insufficient-credit-alert">
    <customer-support-action
      v-if="is_welfare_alert"
      :class="class_name"
      :cta_id="cta_id"
      :title="resolved_title"
    >
      <span v-if="is_welfare_alert">Credito non sufficiente?</span>
      <span v-else-if="show">Da integrare con carta</span>
      <vb-icon
        v-if="show"
        :class="resolved_icon_class"
        :name="'vb-chat'"
        :color="sass('info')"
        :size="18"
      />
    </customer-support-action>
  </span>
</template>

<script>
/**
 * Alpitour-specific credit alert used across quote / pad / card-recap blocks
 * when the user's credit is below the package price.
 *
 * Two visual states driven by `availability.welfare`:
 *   - welfare user  → "Credito non sufficiente?" CTA opening Zendesk chat
 *                     (via `<customer-support-action>`, tracked in Rybbit).
 *   - non-welfare   → "Da integrare con carta" info link with tooltip
 *                     explaining that the difference can be paid at checkout.
 *
 * Visibility rule: rendered only when `availability.is_known` is true AND
 * `availability.total < total_price`. Otherwise renders nothing.
 *
 * BEM class naming: each host block keeps its own BEM prefix
 * (`vb-quote__nocredit`, `pad__footer-nocredit`, `vb-card-recap__nocredit`).
 * Pass `class_name` for the wrapper and optionally `icon_class_name` when
 * the icon's prefix differs from the wrapper's (as in pad.vue, where the
 * icon uses `vb-pad__nocredit-icon` while the wrapper uses
 * `pad__footer-nocredit`). By default the icon class is `${class_name}-icon`.
 */
export default {
  name: "insufficient-credit-alert",

  props: {
    /**
     * Availability object from Vuex / parent — shape: { welfare, is_known, total }.
     */
    availability: {
      type: Object,
      required: true,
    },
    /**
     * Threshold price. The alert shows when `availability.total < total_price`.
     */
    total_price: {
      type: Number,
      required: true,
    },
    /**
     * BEM class applied to the wrapper element.
     */
    class_name: {
      type: String,
      required: true,
    },
    /**
     * Optional BEM class for the icon. Defaults to `${class_name}-icon`.
     */
    icon_class_name: {
      type: String,
      default: "",
    },
    /**
     * Rybbit `cta_id` forwarded to `<customer-support-action>` when the
     * welfare branch is rendered.
     */
    cta_id: {
      type: String,
      required: true,
    },
  },

  computed: {
    show() {
      return (
        this.availability &&
        this.availability.is_known &&
        this.availability.total < this.total_price
      );
    },
    is_welfare_alert() {
      return this.show && this.availability.welfare;
    },
    resolved_title() {
      if (this.is_welfare_alert) {
        return "Contattaci";
      }
      if (this.show) {
        return "Il tuo credito non è sufficiente ma, al checkout, potrai saldare la differenza con la tua carta di credito.";
      }
      return "";
    },
    resolved_icon_class() {
      return this.icon_class_name || `${this.class_name}-icon`;
    },
  },
};
</script>
